import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AudioLines, Image, Paperclip } from 'lucide-react';
import type { MediaAttachment, MediaAttachmentData } from '@openpeepshq/common';
import { useT } from '../../../i18n';
import { ImageEditModal } from '../../form/ImageEditModal';
import { convertToWebpIfHeic } from '../../../lib/canvasUtils';
import { AttachmentCard, type ComposeItem } from './AttachmentCard';
import { isBlobUrl, revokeBlobUrl } from '../attachmentPreview';

export interface ComposeAttachmentsProps {
  attachments: MediaAttachmentData[];
  onChange: (attachments: MediaAttachmentData[]) => void;
  /** Image picker may select several files at once (resource galleries). */
  multipleImages?: boolean;
  /** Audio picker may select several files at once (default: yes). */
  multipleAudio?: boolean;
}

const AUDIO_NAME = /\.(mp3|wav|ogg|oga|m4a|aac|flac|opus|weba)$/i;

const isAudioFile = (file: File): boolean =>
  file.type.startsWith('audio/') || AUDIO_NAME.test(file.name);

function usageForFile(file: File): string {
  if (file.type.startsWith('image/')) return 'post-image';
  if (file.type.startsWith('video/') || file.name.endsWith('.mkv'))
    return 'post-video';
  if (isAudioFile(file)) return 'post-audio';
  return 'post-document';
}

const newKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const readyItem = (attachment: MediaAttachmentData): ComposeItem => ({
  key: newKey(),
  usage: attachment.meta?.usage ?? 'unknown',
  attachment,
  previewUrl: attachment.previewUrl ?? attachment.url ?? undefined,
});

export interface ComposeAttachmentsApi {
  openImagePicker: () => void;
  openAudioPicker: () => void;
  openDocumentPicker: () => void;
  /** True while any attachment is still uploading or processing. */
  pending: boolean;
  /** Attachment cards — render where the previews should appear. */
  previews: ReactNode;
  /** Hidden file inputs + image cropper — render once anywhere in the tree. */
  inputs: ReactNode;
}

/**
 * Owns the attachment list and its upload lifecycle. Each item is shown
 * immediately as a card with byte-upload and server-processing progress
 * (mirrors the Svelte `Attachments` + `AttachmentContainer`), and only ready
 * attachments are reported back via `onChange`. Exposes the toolbar pickers and
 * preview grid separately so callers can lay them out independently.
 */
export function useComposeAttachments({
  attachments,
  onChange,
  multipleImages = false,
  multipleAudio = true,
}: ComposeAttachmentsProps): ComposeAttachmentsApi {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [pendingImage, setPendingImage] = useState<{
    file: File;
    previewUrl: string;
  } | null>(null);

  // `attachments` seeds the list once; thereafter the items are the source of
  // truth (so in-flight upload/processing state isn't clobbered by re-renders).
  const [items, setItems] = useState<ComposeItem[]>(() =>
    attachments.map(readyItem),
  );

  // Report only ready attachments upward; uploading/processing/failed ones stay
  // local until they resolve (matching the Svelte submit-gating model).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const ready = items
      .filter(
        (i) =>
          i.attachment && !i.failed && i.attachment.status !== 'processing',
      )
      .map((i) => i.attachment as MediaAttachmentData);
    onChangeRef.current(ready);
  }, [items]);

  const enqueue = (
    file: File,
    usage: string,
    description?: string,
    previewUrl?: string,
  ) => {
    setItems((prev) => [
      ...prev,
      { key: newKey(), file, usage, description, previewUrl },
    ]);
  };

  const enqueueFile = (file: File) => {
    const usage = usageForFile(file);
    const previewUrl =
      file.type.startsWith('image/') ||
      file.type.startsWith('video/') ||
      isAudioFile(file)
        ? URL.createObjectURL(file)
        : undefined;
    enqueue(
      file,
      usage,
      usage === 'post-document' || usage === 'post-audio'
        ? file.name
        : undefined,
      previewUrl,
    );
  };

  const handleImageSelected = async (file: File) => {
    const processed = await convertToWebpIfHeic(file);
    if (processed.type.startsWith('image/')) {
      setPendingImage({
        file: processed,
        previewUrl: URL.createObjectURL(processed),
      });
      return;
    }
    enqueueFile(processed);
  };

  // A single pick keeps the crop/alt-text flow; a bulk pick is enqueued
  // directly, since per-file cropping isn't practical for a gallery batch.
  const handleImageFilesSelected = async (files: File[]) => {
    const first = files[0];
    if (!first) return;
    if (files.length === 1) {
      await handleImageSelected(first);
      return;
    }
    for (const file of files) {
      enqueueFile(await convertToWebpIfHeic(file));
    }
  };

  const onUploaded = (key: string, attachment: MediaAttachment) =>
    setItems((prev) =>
      prev.map((i) => {
        if (i.key !== key) return i;
        const previewUrl = attachment.previewUrl ?? i.previewUrl;
        if (attachment.previewUrl && isBlobUrl(i.previewUrl)) {
          revokeBlobUrl(i.previewUrl);
        }
        return {
          ...i,
          file: undefined,
          attachment,
          previewUrl,
        };
      }),
    );

  const onProcessed = (key: string, attachment: MediaAttachmentData) =>
    setItems((prev) =>
      prev.map((i) => {
        if (i.key !== key) return i;
        if (attachment.previewUrl && isBlobUrl(i.previewUrl)) {
          revokeBlobUrl(i.previewUrl);
        }
        return {
          ...i,
          attachment,
          previewUrl: attachment.previewUrl ?? undefined,
        };
      }),
    );

  const onFailed = (key: string, error?: string) =>
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, failed: true, error } : i)),
    );

  const onRemove = (key: string) =>
    setItems((prev) => prev.filter((i) => i.key !== key));

  const onUpdate = (key: string, attachment: MediaAttachmentData) =>
    setItems((prev) =>
      prev.map((i) => (i.key === key ? { ...i, attachment } : i)),
    );

  const pending = items.some(
    (i) =>
      (i.file && !i.attachment && !i.failed) ||
      i.attachment?.status === 'processing',
  );

  const previews =
    items.length > 0 ? (
      <div className="grid grid-cols-2 gap-2">
        {items.map((item) => (
          <AttachmentCard
            key={item.key}
            item={item}
            onUploaded={onUploaded}
            onProcessed={onProcessed}
            onFailed={onFailed}
            onRemove={onRemove}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    ) : null;

  const inputs = (
    <>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*,video/*,.mkv"
        multiple={multipleImages}
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files?.length) void handleImageFilesSelected(Array.from(files));
          e.target.value = '';
        }}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac,.opus,.weba"
        multiple={multipleAudio}
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files) {
            Array.from(files).forEach((file) => enqueueFile(file));
          }
          e.target.value = '';
        }}
      />
      <input
        ref={documentInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt,.md,.csv"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) enqueueFile(file);
          e.target.value = '';
        }}
      />
      {pendingImage ? (
        <ImageEditModal
          file={pendingImage.file}
          previewUrl={pendingImage.previewUrl}
          open
          onClose={() => {
            URL.revokeObjectURL(pendingImage.previewUrl);
            setPendingImage(null);
          }}
          onConfirm={(file, description) => {
            URL.revokeObjectURL(pendingImage.previewUrl);
            setPendingImage(null);
            enqueue(file, 'post-image', description, URL.createObjectURL(file));
          }}
        />
      ) : null}
    </>
  );

  return {
    openImagePicker: () => imageInputRef.current?.click(),
    openAudioPicker: () => audioInputRef.current?.click(),
    openDocumentPicker: () => documentInputRef.current?.click(),
    pending,
    previews,
    inputs,
  };
}

export function ComposeAttachments(props: ComposeAttachmentsProps) {
  const t = useT();
  const {
    openImagePicker,
    openAudioPicker,
    openDocumentPicker,
    previews,
    inputs,
  } = useComposeAttachments(props);

  const addImageLabel = t('posts.form.addImage', {
    defaultValue: 'Add image or video',
  });
  const addAudioLabel = t('posts.form.addAudio', {
    defaultValue: 'Add audio',
  });
  const addDocumentLabel = t('posts.form.addDocument', {
    defaultValue: 'Add document',
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          title={addImageLabel}
          aria-label={addImageLabel}
          onClick={openImagePicker}
          className="hover:bg-surface rounded-button p-2"
        >
          <Image className="size-5" aria-hidden="true" />
          <span className="sr-only">{addImageLabel}</span>
        </button>
        <button
          type="button"
          title={addAudioLabel}
          aria-label={addAudioLabel}
          onClick={openAudioPicker}
          className="hover:bg-surface rounded-button p-2"
        >
          <AudioLines className="size-5" aria-hidden="true" />
          <span className="sr-only">{addAudioLabel}</span>
        </button>
        <button
          type="button"
          title={addDocumentLabel}
          aria-label={addDocumentLabel}
          onClick={openDocumentPicker}
          className="hover:bg-surface rounded-button p-2"
        >
          <Paperclip className="size-5" aria-hidden="true" />
          <span className="sr-only">{addDocumentLabel}</span>
        </button>
      </div>

      {previews}
      {inputs}
    </div>
  );
}
